/*
  Sossu Collab section library — shared JS.
  Currently used by: F06 (drag to reveal), F21 (hover index). F09's marquee
  is pure CSS (animation-play-state for pause-on-hover, a custom property
  for direction, a prefers-reduced-motion override) and needs none of this.

  Pattern: one init function per component, each idempotent (guarded by a
  data-ssc-init attribute so calling it again on an already-initialized
  root is a no-op) and scoped to a single root element — so an arbitrary
  number of instances of a component can coexist on one page, each
  independent, matching this library's per-block architecture.

  Re-initialized on Shopify theme editor events (shopify:section:load,
  shopify:block:select — and shopify:section:reorder, belt-and-suspenders,
  since either can re-render a block's markup while editing) so a merchant
  editing colors/settings in the customizer doesn't need a full page
  reload to see F06/F21 working again. Every entry point tolerates a
  missing root, a root with no matching elements, or missing image data —
  it just returns early rather than throwing.
*/
(function () {
  'use strict';

  function initDragToReveal(root) {
    if (!root || root.hasAttribute('data-ssc-init')) return;
    var range = root.querySelector('.ssc-f6__input');
    // Class selector, not [data-ssc-top]: the placeholder SVG shown when
    // no image is picked yet shares the same class but can't carry a
    // custom data attribute (placeholder_svg_tag doesn't support one), so
    // this needs to work identically for either element.
    var top = root.querySelector('.ssc-f6__img-2');
    var line = root.querySelector('[data-ssc-line]');
    if (!range || !top || !line) return;

    var set = function (v) {
      var clamped = Math.max(0, Math.min(100, v));
      top.style.clipPath = 'inset(0 ' + (100 - clamped) + '% 0 0)';
      line.style.left = clamped + '%';
    };

    range.addEventListener('input', function (e) {
      set(+e.target.value);
    });
    set(+range.value);
    root.setAttribute('data-ssc-init', 'true');
  }

  function initHoverIndex(root) {
    if (!root || root.hasAttribute('data-ssc-init')) return;
    var panel = root.querySelector('[data-ssc-panel]');
    var rows = root.querySelectorAll('[data-ssc-row]');
    if (!panel || !rows.length) return;

    // data-ssc-fade only affects HOW a swap looks (a CSS transition
    // duration on .ssc-f21__img, see assets/sossu-collab.css) — the JS
    // logic itself is identical either way, so it isn't read here.

    // Finds the stacked panel <img> for this row's index, creating it
    // (and starting its fetch) the first time — this is what makes
    // "preload on hover intent" work even though images are stacked:
    // nothing for a row is fetched until that row is actually hovered or
    // focused, and once created an image is never re-fetched or mutated
    // again (its srcset is correct from the moment it's added, so there's
    // nothing left that could go stale the way a single swapped <img> did).
    var getOrCreatePanelImg = function (row) {
      var index = row.getAttribute('data-row-index');
      if (!index) return null;
      var existing = panel.querySelector('.ssc-f21__img[data-row-index="' + index + '"]');
      if (existing) return existing;

      var src = row.getAttribute('data-img');
      if (!src) return null;

      var img = document.createElement('img');
      img.className = 'ssc-f21__img';
      img.setAttribute('data-row-index', index);
      img.alt = row.getAttribute('data-alt') || '';
      img.loading = 'lazy';
      var srcset = row.getAttribute('data-srcset');
      if (srcset) {
        img.srcset = srcset;
        img.sizes = '50vw';
      }
      img.style.opacity = '0';
      img.src = src; // starts the fetch immediately — the "preload" step
      panel.appendChild(img);
      return img;
    };

    var showPanelImg = function (img) {
      panel.querySelectorAll('.ssc-f21__img').forEach(function (other) {
        other.style.opacity = other === img ? '1' : '0';
      });
    };

    var activate = function (row) {
      for (var i = 0; i < rows.length; i++) {
        var active = rows[i] === row;
        rows[i].classList.toggle('is-active', active);
        // aria-pressed only belongs on the role="button" (non-link) rows
        // — Liquid never renders it on <a> rows, so don't add it here either.
        if (rows[i].tagName !== 'A') {
          rows[i].setAttribute('aria-pressed', active ? 'true' : 'false');
        }
      }
      var img = getOrCreatePanelImg(row);
      if (!img) return;
      if (img.complete) {
        showPanelImg(img);
      } else {
        img.addEventListener('load', function () {
          showPanelImg(img);
        }, { once: true });
      }
    };

    var desktopQuery = window.matchMedia('(min-width: 1025px)');

    var bindRow = function (row) {
      row.addEventListener('mouseenter', function () {
        if (!desktopQuery.matches) return;
        activate(row);
      });
      row.addEventListener('focus', function () {
        if (!desktopQuery.matches) return;
        activate(row);
      });
      row.addEventListener('click', function () {
        if (!desktopQuery.matches) return;
        if (row.tagName === 'A') return; // real link — let it navigate
        activate(row);
      });
      row.addEventListener('keydown', function (e) {
        if (!desktopQuery.matches) return;
        if (row.tagName === 'A') return; // native Enter/Space handling
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          activate(row);
        }
      });
    };

    for (var j = 0; j < rows.length; j++) {
      bindRow(rows[j]);
    }

    root.setAttribute('data-ssc-init', 'true');
  }

  function initAll(scope) {
    scope = scope || document;
    scope.querySelectorAll('.ssc-f6').forEach(initDragToReveal);
    scope.querySelectorAll('.ssc-f21').forEach(initHoverIndex);
  }

  function reinit(scope) {
    (scope || document).querySelectorAll('.ssc-f6[data-ssc-init], .ssc-f21[data-ssc-init]').forEach(function (el) {
      el.removeAttribute('data-ssc-init');
    });
    initAll(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    reinit(e.target);
  });
  document.addEventListener('shopify:block:select', function (e) {
    reinit(e.target && e.target.closest ? e.target.closest('.shopify-section') || document : document);
  });
  document.addEventListener('shopify:section:reorder', function () {
    reinit(document);
  });
})();
